import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Eye, EyeOff, X, CheckCircle, Loader, Mail } from 'lucide-react'
import { useAuthStore } from '../store/authStore'
import { supportTicketService, type PublicTicketCreate } from '../../../services/supportTicketService'
import type { TicketCategory, TicketPriority } from '../../../types/api.types'
import logo from '../../../assets/logo.png'

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
})

type LoginFormData = z.infer<typeof loginSchema>

const CATEGORIES: { value: TicketCategory; label: string }[] = [
  { value: 'TECHNICAL', label: 'Technical Issue' },
  { value: 'BILLING', label: 'Billing' },
  { value: 'ACCOUNT', label: 'Account' },
  { value: 'FEATURE_REQUEST', label: 'Feature Request' },
  { value: 'OTHER', label: 'Other' },
]

const PRIORITIES: { value: TicketPriority; label: string }[] = [
  { value: 'LOW', label: 'Low' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'HIGH', label: 'High' },
  { value: 'CRITICAL', label: 'Critical' },
]

function ForgotPasswordModal({ onClose }: { onClose: () => void }): JSX.Element {
  const forgotPassword = useAuthStore((s) => s.forgotPassword)
  const [email, setEmail] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const isDev = import.meta.env.DEV
  const resetBaseUrl = import.meta.env.VITE_PORTAL_RESET_PASSWORD_URL as string | undefined
  let resetBaseOrigin = ''
  try { resetBaseOrigin = resetBaseUrl ? new URL(resetBaseUrl).origin : '' } catch { /* */ }
  const showDevWarning = isDev && !!resetBaseOrigin && resetBaseOrigin !== window.location.origin

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSubmitting(true); setError('')
    const result = await forgotPassword(email)
    setSubmitting(false)
    if (result.success) {
      setSuccess(result.message || 'If an account with that email exists, a reset link has been sent.')
    } else {
      setError(result.error || 'Unable to process request.')
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-sm rounded-xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <h2 className="text-[16px] font-bold text-gray-900">Forgot password</h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={18} />
          </button>
        </div>

        {success ? (
          <div className="flex flex-col items-center px-6 py-10 text-center">
            <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-green-100">
              <Mail size={26} className="text-green-600" />
            </div>
            <h3 className="text-[16px] font-bold text-gray-900">Check your email</h3>
            <p className="mt-2 text-[13px] text-gray-500">{success}</p>
            <button
              type="button"
              onClick={onClose}
              className="mt-6 rounded-lg bg-brand-green px-6 py-2 text-[13px] font-semibold text-black hover:opacity-80"
            >
              Back to sign in
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 p-6">
            <p className="text-[13px] text-gray-500">Enter your email and we'll send a reset link.</p>

            {showDevWarning && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-700">
                Reset emails point to <strong>{resetBaseOrigin}</strong> — set{' '}
                <code>VITE_PORTAL_RESET_PASSWORD_URL</code> for local testing.
              </div>
            )}

            <div>
              <label className="mb-1.5 block text-[13px] font-medium text-gray-700">Email Address</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-10 w-full rounded-lg border border-[#E5E7EB] bg-white px-4 text-[14px] text-gray-900 outline-none transition-colors focus:border-brand-green"
                placeholder="you@company.com"
                autoFocus
              />
            </div>

            {error && (
              <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-600">{error}</p>
            )}

            <div className="flex gap-3 pt-1">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 rounded-lg border border-gray-200 py-2 text-[13px] text-gray-600 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-brand-green py-2 text-[13px] font-semibold text-black hover:opacity-80 disabled:opacity-50"
              >
                {submitting && <Loader size={13} className="animate-spin" />}
                Send reset link
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

function SupportModal({ onClose }: { onClose: () => void }): JSX.Element {
  const [form, setForm] = useState<PublicTicketCreate>({
    submitted_by_name: '',
    submitted_by_email: '',
    submitted_by_phone: '',
    organization_name: '',
    category: 'TECHNICAL',
    priority: 'MEDIUM',
    subject: '',
    description: '',
  })
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [ticketNumber, setTicketNumber] = useState('')

  function set(key: keyof PublicTicketCreate, value: string) {
    setForm((f) => ({ ...f, [key]: value }))
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSubmitting(true); setError('')
    try {
      const ticket = await supportTicketService.submitPublic({
        ...form,
        submitted_by_phone: form.submitted_by_phone || undefined,
        organization_name: form.organization_name || undefined,
      })
      setTicketNumber(ticket.ticket_number)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to submit ticket. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg rounded-xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <h2 className="text-[16px] font-bold text-gray-900">Contact Support</h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={18} />
          </button>
        </div>

        {ticketNumber ? (
          <div className="flex flex-col items-center px-6 py-10 text-center">
            <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-green-100">
              <CheckCircle size={28} className="text-green-600" />
            </div>
            <h3 className="text-[17px] font-bold text-gray-900">Ticket Submitted</h3>
            <p className="mt-2 text-[13px] text-gray-500">Your reference number:</p>
            <p className="mt-1 font-mono text-[20px] font-bold text-brand-green">{ticketNumber}</p>
            <p className="mt-3 max-w-xs text-[12px] text-gray-400">
              A confirmation has been sent to <strong>{form.submitted_by_email}</strong>. Our team will get back to you shortly.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="mt-6 rounded-lg bg-brand-green px-6 py-2 text-[13px] font-semibold text-black hover:opacity-80"
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 p-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-1 block text-[12px] font-medium text-gray-700">Your Name *</label>
                <input
                  required
                  value={form.submitted_by_name}
                  onChange={(e) => set('submitted_by_name', e.target.value)}
                  placeholder="Ahmad Hassan"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-[13px] outline-none focus:border-brand-green"
                />
              </div>
              <div>
                <label className="mb-1 block text-[12px] font-medium text-gray-700">Email *</label>
                <input
                  required
                  type="email"
                  value={form.submitted_by_email}
                  onChange={(e) => set('submitted_by_email', e.target.value)}
                  placeholder="you@company.com"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-[13px] outline-none focus:border-brand-green"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-1 block text-[12px] font-medium text-gray-700">Phone</label>
                <input
                  value={form.submitted_by_phone}
                  onChange={(e) => set('submitted_by_phone', e.target.value)}
                  placeholder="+971501234567"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-[13px] outline-none focus:border-brand-green"
                />
              </div>
              <div>
                <label className="mb-1 block text-[12px] font-medium text-gray-700">Organisation</label>
                <input
                  value={form.organization_name}
                  onChange={(e) => set('organization_name', e.target.value)}
                  placeholder="Company name"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-[13px] outline-none focus:border-brand-green"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-1 block text-[12px] font-medium text-gray-700">Category *</label>
                <select
                  value={form.category}
                  onChange={(e) => set('category', e.target.value)}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-[13px] outline-none focus:border-brand-green"
                >
                  {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-[12px] font-medium text-gray-700">Priority *</label>
                <select
                  value={form.priority}
                  onChange={(e) => set('priority', e.target.value)}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-[13px] outline-none focus:border-brand-green"
                >
                  {PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className="mb-1 block text-[12px] font-medium text-gray-700">Subject *</label>
              <input
                required
                value={form.subject}
                onChange={(e) => set('subject', e.target.value)}
                placeholder="Brief summary of the issue"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-[13px] outline-none focus:border-brand-green"
              />
            </div>

            <div>
              <label className="mb-1 block text-[12px] font-medium text-gray-700">Description *</label>
              <textarea
                required
                rows={4}
                value={form.description}
                onChange={(e) => set('description', e.target.value)}
                placeholder="Describe the issue in detail..."
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-[13px] outline-none focus:border-brand-green"
              />
            </div>

            {error && (
              <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-600">{error}</p>
            )}

            <div className="flex justify-end gap-3 pt-1">
              <button type="button" onClick={onClose} className="rounded-lg border border-gray-200 px-4 py-2 text-[13px] text-gray-600 hover:bg-gray-50">
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="flex items-center gap-1.5 rounded-lg bg-brand-green px-4 py-2 text-[13px] font-semibold text-black hover:opacity-80 disabled:opacity-50"
              >
                {submitting && <Loader size={13} className="animate-spin" />}
                Submit
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

export function LoginPage(): JSX.Element {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { login, user, isAuthenticated } = useAuthStore()
  const [serverError, setServerError] = useState('')
  const [sessionNotice, setSessionNotice] = useState<string | null>(null)
  const [isPasswordVisible, setIsPasswordVisible] = useState<boolean>(false)
  const [showSupport, setShowSupport] = useState(false)
  const [showForgot, setShowForgot] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  })

  useEffect(() => {
    const token =
      searchParams.get('token') || searchParams.get('reset_token') || searchParams.get('t')
    if (token) {
      navigate(`/reset-password?token=${encodeURIComponent(token)}`, { replace: true })
      return
    }

    if (searchParams.get('expired') === '1') {
      setSessionNotice('Your session expired. Please sign in again.')
    } else if (searchParams.get('session') === '1') {
      setSessionNotice('Please sign in to continue.')
    }
  }, [searchParams, navigate])

  useEffect(() => {
    // "/" decides where this user belongs — it knows about roles and about the
    // property vertical, which this page does not.
    if (isAuthenticated && user) {
      navigate('/', { replace: true })
    }
  }, [isAuthenticated, user, navigate])

  const onSubmit = async (formData: LoginFormData): Promise<void> => {
    setServerError('')
    const result = await login(formData.email, formData.password)
    if (!result.success) {
      setServerError(result.error || 'Login failed.')
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-white px-4">
      <div className="w-full max-w-md">
        <div className="mb-8 flex justify-center">
          <img src={logo} alt="QuickComm" className="h-10 w-auto" />
        </div>

        <div className="rounded-xl border border-[#E5E7EB] bg-white p-8 shadow-sm">
          <h1 className="mb-1 text-[28px] font-bold leading-tight text-gray-900">Welcome back</h1>
          <p className="mb-6 text-[14px] text-gray-500">Sign in to your account</p>

          {sessionNotice && (
            <div className="mb-4 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[13px] text-amber-700">
              <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-amber-500" />
              {sessionNotice}
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            <div>
              <label htmlFor="email" className="mb-1.5 block text-[13px] font-medium text-gray-700">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                {...register('email')}
                className="h-10 w-full rounded-lg border border-[#E5E7EB] bg-white px-4 text-[14px] text-gray-900 outline-none transition-colors focus:border-brand-green"
                placeholder="you@company.com"
              />
              {errors.email && (
                <p className="mt-1 text-xs text-red-600">{errors.email.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="password" className="mb-1.5 block text-[13px] font-medium text-gray-700">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={isPasswordVisible ? 'text' : 'password'}
                  {...register('password')}
                  className="h-10 w-full rounded-lg border border-[#E5E7EB] bg-white px-4 pr-10 text-[14px] text-gray-900 outline-none transition-colors focus:border-brand-green"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setIsPasswordVisible((v) => !v)}
                  aria-label={isPasswordVisible ? 'Hide password' : 'Show password'}
                  aria-pressed={isPasswordVisible}
                  className="absolute inset-y-0 right-2 flex items-center px-1.5 text-gray-400 hover:text-gray-700 focus:outline-none focus:text-gray-700"
                >
                  {isPasswordVisible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-xs text-red-600">{errors.password.message}</p>
              )}
              <div className="mt-2 text-right">
                <button
                  type="button"
                  onClick={() => setShowForgot(true)}
                  className="text-xs font-medium text-brand-green hover:underline"
                >
                  Forgot password?
                </button>
              </div>
            </div>

            {serverError && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                <p className="text-[13px] text-red-600">{serverError}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="h-10 w-full rounded-lg bg-brand-green text-[14px] font-semibold text-black transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSubmitting ? 'Signing In...' : 'Sign In'}
            </button>
          </form>
        </div>

        <p className="mt-5 text-center text-[12px] text-gray-400">
          Having trouble?{' '}
          <button
            type="button"
            onClick={() => setShowSupport(true)}
            className="font-medium text-brand-green hover:underline"
          >
            Contact Support
          </button>
        </p>
      </div>

      {showForgot && <ForgotPasswordModal onClose={() => setShowForgot(false)} />}
      {showSupport && <SupportModal onClose={() => setShowSupport(false)} />}
    </div>
  )
}
